import {
  Checkbox,
  CheckboxTypography,
  FormGridItem,
  type FormGridItemProps,
  MenuItem,
  Select,
  type SelectProps,
  useSelectValidator,
} from '@ace-clublabs/shared-component-library';
import { getRequiredFieldSx } from '../utils';

type SelectOption = {
  label: string;
  value: string;
};

const typeOfTripOptions: SelectOption[] = [
  { label: 'Cruises', value: 'Cruise' },
  { label: 'Guided tours', value: 'Guided tour' },
  { label: 'Hotels', value: 'Hotel' },
  { label: 'Flights', value: 'Flight' },
  { label: 'Rental cars', value: 'Rental car' },
  { label: 'Building a vacation package', value: 'Build a vacation package' },
  { label: 'Day trips and activities', value: 'Day trips and activities' },
];

const typeOfTripOptionsMap: Record<string, string> = typeOfTripOptions.reduce(
  (acc, option) => {
    acc[option.value] = option.label;
    return acc;
  },
  {} as Record<string, string>,
);

const selectMenuProps: SelectProps['MenuProps'] = {
  sx: {
    '& .MuiMenu-paper': {
      maxHeight: {
        xs: 51 * 3,
        lg: 51 * 4,
      },
    },
  },
  PaperProps: { className: 'MuiSelect-Menu-paper' },
};

export interface TypeOfTripFieldProps {
  value?: string[];
  onChange: (value: string[]) => void;
  required?: boolean;
  fieldProps?: Partial<SelectProps<string[]>>;
  validatorProps?: Parameters<typeof useSelectValidator>[0];
  gridItemProps?: Partial<FormGridItemProps>;
}

export const TypeOfTripField: React.FC<TypeOfTripFieldProps> = ({
  value,
  onChange,
  required = false,
  fieldProps,
  validatorProps,
  gridItemProps,
}) => {
  const label = 'What can we help you with?';
  const validator = useSelectValidator({
    label,
    required,
    ...validatorProps,
  });

  return (
    <FormGridItem
      size={{ xs: 12, lg: 6 }}
      {...gridItemProps}
      sx={getRequiredFieldSx(required)}
    >
      <Select<string[]>
        label={label}
        placeholder='Types of trips'
        fullWidth
        multiple
        value={value || []}
        fullScreenThreshold={8}
        validator={validator}
        renderValue={(selected) => {
          if (!selected || selected.length === 0) {
            return 'Types of trips';
          }
          return selected
            .map((val) => {
              const label = typeOfTripOptionsMap[val];
              return label ? label : val;
            })
            .join(', ');
        }}
        onChange={(e) => onChange(e.target.value as string[])}
        name='typeOfTrip'
        data-testid='typeOfTrip'
        MenuProps={selectMenuProps}
        {...fieldProps}
      >
        {typeOfTripOptions.map(({ value: optionValue, label }) => (
          <MenuItem key={optionValue} value={optionValue}>
            <Checkbox
              checked={value?.includes(optionValue) || false}
              variant='text'
              label={
                <CheckboxTypography
                  sx={{
                    fontSize: '16px',
                  }}
                >
                  {label}
                </CheckboxTypography>
              }
              value={optionValue}
              name={optionValue}
              slotProps={{
                formControlLabelProps: {
                  sx: {
                    outline: 'none !important',
                    p: '0 !important',
                    pointerEvents: 'none',
                  },
                },
              }}
            />
          </MenuItem>
        ))}
      </Select>
    </FormGridItem>
  );
};
